import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Send,
  Search,
  Paperclip,
  Image,
  FileText,
  File,
  Trash2,
  Download,
  X,
  MoreVertical,
  MessageCircle,
  Reply,
  Check,
  CheckCheck,
  Bell,
  Filter,
  ChevronDown,
  Folder
} from 'lucide-react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';

// File type detection
const getFileType = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  return 'other';
};

// File icon
const FileIcon = ({ type, size = 18 }) => {
  switch (type) {
    case 'image': return <Image color="#00d4aa" size={size} />;
    case 'pdf': return <FileText color="#ef4444" size={size} />;
    default: return <File color="#9ca3af" size={size} />;
  }
};

// Date separator
const DateSeparator = ({ date }) => {
  const today = new Date();
  const msgDate = new Date(date);
  
  let label;
  if (msgDate.toDateString() === today.toDateString()) {
    label = "Aujourd'hui";
  } else {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (msgDate.toDateString() === yesterday.toDateString()) {
      label = 'Hier';
    } else {
      label = msgDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
      <Divider sx={{ flex: 1 }} />
      <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', textTransform: 'capitalize' }}>
        {label}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  );
};

// Message bubble component
const MessageBubble = ({ 
  message, 
  isOwn, 
  isAdmin, 
  onReply, 
  onDelete, 
  onAssociateAttachment,
  currentUserId 
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  
  const isSystem = message.type === 'SYSTEM';
  const isImportant = message.type === 'IMPORTANT';

  // System message style
  if (isSystem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <Chip
          icon={<Bell size={14} />}
          label={message.content}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 193, 7, 0.1)',
            color: '#ffc107',
            fontSize: '0.75rem',
            '& .MuiChip-icon': { color: '#ffc107' }
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: isOwn ? 'row-reverse' : 'row',
      alignItems: 'flex-start',
      gap: 1,
      mb: 1.5
    }}>
      {/* Avatar */}
      <Tooltip title={message.author.name}>
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: message.author.role === 'CLIENT' ? 'primary.main' : 'secondary.main',
            fontSize: '0.875rem'
          }}
        >
          {message.author.name.charAt(0).toUpperCase()}
        </Avatar>
      </Tooltip>

      {/* Message content */}
      <Box sx={{ maxWidth: '70%' }}>
        {/* Author name (only for non-own messages) */}
        {!isOwn && (
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, mb: 0.5, display: 'block' }}>
            {message.author.name}
            {message.author.role !== 'CLIENT' && (
              <Chip label="Admin" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
            )}
          </Typography>
        )}

        {/* Reply preview */}
        {message.replyTo && (
          <Paper
            sx={{
              p: 1,
              mb: 0.5,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              borderLeft: '2px solid',
              borderColor: 'primary.main',
              borderRadius: '4px 8px 8px 4px'
            }}
          >
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
              {message.replyTo.authorName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
              {message.replyTo.content}
            </Typography>
          </Paper>
        )}

        {/* Message bubble */}
        <Paper
          sx={{
            p: 1.5,
            bgcolor: isOwn ? 'primary.main' : isImportant ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.03)',
            color: isOwn ? '#0a0a0f' : 'text.primary',
            borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            border: isImportant ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(255, 255, 255, 0.06)',
            position: 'relative'
          }}
        >
          {message.content && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message.content}
            </Typography>
          )}

          {/* Attachments */}
          {message.attachments.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: message.content ? 1 : 0 }}>
              {message.attachments.map((att) => (
                <Box 
                  key={att.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    bgcolor: isOwn ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isOwn ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.08)' }
                  }}
                  onClick={() => window.open(att.url, '_blank')}
                >
                  {getFileType(att.name) === 'image' ? (
                    <Box
                      component="img"
                      src={att.url}
                      alt={att.name}
                      sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                    />
                  ) : (
                    <FileIcon type={getFileType(att.name)} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 500 }} noWrap>
                      {att.name}
                    </Typography>
                    {att.quoteSectionName && (
                      <Chip 
                        icon={<Folder size={10} />}
                        label={att.quoteSectionName} 
                        size="small" 
                        sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} 
                      />
                    )}
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(att.url, '_blank'); }}>
                    <Download size={14} />
                  </IconButton>
                  {isAdmin && !att.quoteSectionId && (
                    <Tooltip title="Associer à une section">
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); onAssociateAttachment(att); }}
                      >
                        <Folder size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {/* Actions menu button */}
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.2s',
              '.MuiPaper-root:hover &': { opacity: 1 }
            }}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertical size={14} />
          </IconButton>
        </Paper>

        {/* Time and read status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 1, justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {new Date(message.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            {message.isEdited && ' (modifié)'}
          </Typography>
          {isOwn && (
            message.readBy.length > 1 ? (
              <Tooltip title={`Lu par ${message.readBy.length - 1} personne(s)`}>
                <CheckCheck size={14} color="#00d4aa" />
              </Tooltip>
            ) : (
              <Check size={14} color="#9ca3af" />
            )
          )}
        </Box>
      </Box>

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { onReply(message); setMenuAnchor(null); }}>
          <ListItemIcon><Reply size={16} /></ListItemIcon>
          <ListItemText>Répondre</ListItemText>
        </MenuItem>
        {(isOwn || isAdmin) && (
          <MenuItem onClick={() => { onDelete(message.id); setMenuAnchor(null); }} sx={{ color: '#ef4444' }}>
            <ListItemIcon><Trash2 size={16} color="#ef4444" /></ListItemIcon>
            <ListItemText>Supprimer</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

function EchangesPage() {
  const { id: submissionId } = useParams();
  const { user } = useAuth();
  const { socket, joinSubmission, leaveSubmission } = useSocket();
  const messagesEndRef = useRef();
  const fileInputRef = useRef();

  const [messages, setMessages] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Input state
  const [messageText, setMessageText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentSectionId, setAttachmentSectionId] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // Dialogs
  const [associateDialogOpen, setAssociateDialogOpen] = useState(false);
  const [attachmentToAssociate, setAttachmentToAssociate] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState('');

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('portalToken');

      // Fetch submission
      const subRes = await fetch(`/api/submissions/${submissionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubmission(subData);
      }

      // Fetch messages
      const msgRes = await fetch(`/api/submissions/${submissionId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (msgRes.ok) {
        const msgData = await msgRes.json();
        setMessages(msgData);
      }

      // Fetch sections for attachment association
      const secRes = await fetch(`/api/submissions/${submissionId}/messages/sections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (secRes.ok) {
        const secData = await secRes.json();
        setSections(secData);
      }

      // Mark all as read
      await fetch(`/api/submissions/${submissionId}/messages/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [submissionId]);

  // Socket: join room and listen for new messages
  useEffect(() => {
    if (!socket || !submissionId) return;

    joinSubmission(submissionId);

    const handleNewMessage = (data) => {
      if (data.submissionId === submissionId && data.message) {
        // Ajouter le message s'il n'existe pas déjà
        setMessages(prev => {
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }
    };

    socket.on('newMessage', handleNewMessage);

    return () => {
      leaveSubmission(submissionId);
      socket.off('newMessage', handleNewMessage);
    };
  }, [socket, submissionId, joinSubmission, leaveSubmission]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Filter messages
  const filteredMessages = useMemo(() => {
    return messages.filter(msg => {
      const matchesSearch = msg.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.author.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filter === 'unread') return !msg.isReadByMe && matchesSearch;
      if (filter === 'attachments') return msg.attachments.length > 0 && matchesSearch;
      if (filter === 'system') return msg.type === 'SYSTEM' && matchesSearch;
      return matchesSearch;
    });
  }, [messages, searchTerm, filter]);

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentDate = null;

    filteredMessages.forEach(msg => {
      const msgDate = new Date(msg.createdAt).toDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: 'date', date: msg.createdAt });
      }
      groups.push({ type: 'message', message: msg });
    });

    return groups;
  }, [filteredMessages]);

  const handleSend = async () => {
    if (!messageText.trim() && !attachmentFile) return;

    setSending(true);
    try {
      const token = localStorage.getItem('portalToken');

      if (attachmentFile) {
        // Send with attachment
        const formData = new FormData();
        formData.append('file', attachmentFile);
        formData.append('content', messageText.trim());
        if (replyTo) formData.append('replyToId', replyTo.id);
        if (isAdmin && attachmentSectionId) formData.append('quoteSectionId', attachmentSectionId);

        await fetch(`/api/submissions/${submissionId}/messages/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      } else {
        // Send text only
        await fetch(`/api/submissions/${submissionId}/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: messageText.trim(),
            replyToId: replyTo?.id
          })
        });
      }

      setMessageText('');
      setReplyTo(null);
      setAttachmentFile(null);
      setAttachmentSectionId('');
      fetchData();
    } catch (err) {
      console.error('Send error:', err);
    }
    setSending(false);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/messages/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleAssociateAttachment = async () => {
    if (!attachmentToAssociate || !selectedSectionId) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/messages/attachments/${attachmentToAssociate.id}/section`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ quoteSectionId: selectedSectionId })
      });
      setAssociateDialogOpen(false);
      setAttachmentToAssociate(null);
      setSelectedSectionId('');
      fetchData();
    } catch (err) {
      console.error('Associate error:', err);
    }
  };

  const openAssociateDialog = (attachment) => {
    setAttachmentToAssociate(attachment);
    setSelectedSectionId('');
    setAssociateDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachmentFile(file);
    }
    e.target.value = '';
  };

  const unreadCount = messages.filter(m => !m.isReadByMe).length;

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement...</Typography>;
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to={isAdmin ? '/admin/submissions' : '/portal/submissions'} color="inherit" underline="hover">
          Devis
        </Link>
        <Link
          component={RouterLink}
          to={isAdmin ? `/admin/submissions/${submissionId}` : `/portal/submissions/${submissionId}`}
          color="inherit"
          underline="hover"
        >
          {submission?.title || 'Devis'}
        </Link>
        <Typography color="text.primary">Échanges</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MessageCircle size={32} color="#00d4aa" />
            Échanges
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {submission?.title} - {messages.length} message{messages.length !== 1 ? 's' : ''}
            {unreadCount > 0 && ` (${unreadCount} non lu${unreadCount !== 1 ? 's' : ''})`}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />

          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(e, val) => val && setFilter(val)}
            size="small"
          >
            <ToggleButton value="all">Tous</ToggleButton>
            <ToggleButton value="unread">Non lus</ToggleButton>
            <ToggleButton value="attachments">
              <Paperclip size={14} style={{ marginRight: 4 }} /> Fichiers
            </ToggleButton>
            <ToggleButton value="system">
              <Bell size={14} style={{ marginRight: 4 }} /> Système
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* Messages area */}
      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(255, 255, 255, 0.01)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        {/* Messages list */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {groupedMessages.length > 0 ? (
            <>
              {groupedMessages.map((item, idx) => (
                item.type === 'date' ? (
                  <DateSeparator key={`date-${idx}`} date={item.date} />
                ) : (
                  <MessageBubble
                    key={item.message.id}
                    message={item.message}
                    isOwn={item.message.author.id === user.id}
                    isAdmin={isAdmin}
                    currentUserId={user.id}
                    onReply={setReplyTo}
                    onDelete={handleDelete}
                    onAssociateAttachment={openAssociateDialog}
                  />
                )
              ))}
              <div ref={messagesEndRef} />
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <MessageCircle size={48} color="#6b7280" style={{ marginBottom: 16 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                {searchTerm || filter !== 'all' ? 'Aucun message trouvé' : 'Aucun message'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || filter !== 'all'
                  ? 'Essayez avec d\'autres filtres'
                  : 'Commencez la conversation'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Input area */}
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.06)', bgcolor: 'rgba(0, 0, 0, 0.2)' }}>
          {/* Reply preview */}
          {replyTo && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                p: 1,
                bgcolor: 'rgba(0, 212, 170, 0.1)',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: 'primary.main'
              }}
            >
              <Reply size={16} color="#00d4aa" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                  {replyTo.author.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                  {replyTo.content}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyTo(null)}>
                <X size={16} />
              </IconButton>
            </Box>
          )}

          {/* Attachment preview */}
          {attachmentFile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                p: 1,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 1
              }}
            >
              <Paperclip size={16} />
              <Typography variant="caption" sx={{ flex: 1 }} noWrap>
                {attachmentFile.name}
              </Typography>
              {isAdmin && sections.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={attachmentSectionId}
                    onChange={(e) => setAttachmentSectionId(e.target.value)}
                    displayEmpty
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="">
                      <em>Non lié</em>
                    </MenuItem>
                    {sections.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton size="small" onClick={() => { setAttachmentFile(null); setAttachmentSectionId(''); }}>
                <X size={16} />
              </IconButton>
            </Box>
          )}

          {/* Input row */}
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.txt,.md,.zip,.rar"
            />
            <IconButton onClick={() => fileInputRef.current.click()} color="primary">
              <Paperclip size={20} />
            </IconButton>

            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Écrivez votre message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.03)'
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending || (!messageText.trim() && !attachmentFile)}
              sx={{ minWidth: 100 }}
            >
              {sending ? '...' : 'Envoyer'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Associate attachment dialog */}
      <Dialog
        open={associateDialogOpen}
        onClose={() => setAssociateDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 350 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Folder size={20} color="#00d4aa" />
          Associer à une section
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {attachmentToAssociate?.name}
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Section</InputLabel>
            <Select
              value={selectedSectionId}
              label="Section"
              onChange={(e) => setSelectedSectionId(e.target.value)}
            >
              {sections.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssociateDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAssociateAttachment} disabled={!selectedSectionId}>
            Associer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EchangesPage;
